import { Button, HStack, Stack, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FullModal } from '$/components/common/Modals/Modal';
import {
  HookFormInput,
  type HookFormInputProps,
} from '$/components/core/Form/HookFormInput';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { updateTeacherInvitation } from '$/services/usecases/invitation';
import { type TeacherInvitationRequest } from '$/services/usecases/invitation/mapper/invitation';
import { emailFormatRegex } from '$/utils/regexUtils';

interface Props {
  invitation: TeacherInvitationRequest;
  isOpen: boolean;
  onClose: () => void;
}

type EditInviteFormData = {
  email: string;
  firstName: string;
  lastName: string;
};

const Input = (props: HookFormInputProps<EditInviteFormData>) => (
  <HookFormInput<EditInviteFormData> {...props} />
);

export const EditTeacherInvitationModal: FC<Props> = ({
  invitation,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { cloudMessageTranslation } = useCloudMessageTranslation();

  const formMethods = useForm<EditInviteFormData>({
    mode: 'onTouched',
    values: {
      email: invitation.email,
      firstName: invitation.firstName,
      lastName: invitation.lastName,
    },
  });

  const onCloseModal = () => {
    onClose();
    formMethods.reset();
  };

  const { mutate: updateInvite, isPending } = useMutation({
    mutationFn: (data: EditInviteFormData) =>
      updateTeacherInvitation({ id: invitation.id, ...data }),
    onSuccess: (data) => {
      if (!data.isSuccessful) {
        toast(cloudMessageTranslation(data.response.message), 'error', '');
        return;
      }
      toast(t('school.teachers.editInvitationSuccess'), 'success', '');
      onCloseModal();
    },
  });

  return (
    <FullModal
      isOpen={isOpen}
      onClose={onCloseModal}
      header={t('school.teachers.editInvitationModal.title')}
      size='xl'
      isScrollable={false}
    >
      <FormProvider {...formMethods}>
        <Stack
          as='form'
          gap='4'
          onSubmit={formMethods.handleSubmit((data) => updateInvite(data))}
        >
          <HStack w='full'>
            <Input
              accessor='firstName'
              label={t('dashboard.authentication.firstNameLabel')}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
            <Input
              accessor='lastName'
              label={t('dashboard.authentication.lastNameLabel')}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
          </HStack>
          <Input
            accessor='email'
            label={t('dashboard.authentication.emailLabel')}
            registerOptions={{
              required: t('dashboard.authentication.errorMessages.isRequired'),
              pattern: {
                value: emailFormatRegex,
                message: t(
                  'dashboard.authentication.errorMessages.wrongFormat',
                ),
              },
            }}
          />
          <Text color='lighterText' fontSize='sm'>
            {t('school.teachers.editInvitationModal.emailChangeInfo')}
          </Text>
          <HStack justify='space-between' w='full' mt='10'>
            <Button
              isDisabled={isPending}
              onClick={onCloseModal}
              type='button'
              variant='ghost'
            >
              {t('general.cancel')}
            </Button>
            <Button
              isDisabled={!formMethods.formState.isValid}
              isLoading={isPending}
              type='submit'
            >
              {t('general.save')}
            </Button>
          </HStack>
        </Stack>
      </FormProvider>
    </FullModal>
  );
};
